<template>
  <div class="global-comment">
    <v-btn
      v-if="showAddSentences && !isDisabled"
      text
      small
      class="mb-3"
      color="secondary"
      @click="onAddAutoSentenceClick"
    >
      {{ $t('automaticSentences.addAutomaticSentence') }}
      <v-icon
        right
        small
      >
        add
      </v-icon>
    </v-btn>
    <quillEditor
      id="textEditor"
      :key="editorKey"
      v-model="message"
      class="mb-5"
      :disabled="isDisabled"
      :options="getOptions"
      :data-cy="editorDataCy"
      @change="$emit('commentChange', message)"
    />
    <CustomFileInput
      v-if="canUploadImages && !isDisabled"
      v-model="selectedImages"
      multiple
      :backendFiles="mutableComment ? mutableComment.files : []"
      :padding="20"
      :data-cy="fileInputDataCy"
      :filesDataCy="filesDataCy"
      @removeFiles="removeFiles"
      @change="$emit('commentChange', selectedImages, true)"
    />
    <CustomFileInput
      v-else
      v-model="selectedImages"
      :preview="true"
      multiple
      :backendFiles="mutableComment ? mutableComment.files : []"
      :padding="20"
      data-cy="pictureFileWO"
      @removeFiles="removeFiles"
    />
    <div
      class="text-end"
    >
      <v-btn
        v-if="showSaveBtn && hasPermission('comments.edit')"
        color="secondary"
        style="text-transform: capitalize !important;"
        :disabled="isBtnDisabled"
        :loading="isUploading"
        :data-cy="`${saveBtnDataCy}-${position}`"
        @click="() => onSaveClick(workOrderId)"
      >
        {{ $t('forms.btn.save') }}
      </v-btn>
    </div>
  </div>
</template>

<script>
import 'quill/dist/quill.core.css';
import 'quill/dist/quill.snow.css';
import 'quill/dist/quill.bubble.css';
import Vue from 'vue';
import { quillEditor } from 'vue-quill-editor';
import { mapGetters } from 'vuex';
import AddAutomaticSentenceForm from '@/components/sites/visitSummaryComponents/AddAutomaticSentenceForm';
import CustomFileInput from '@/components/shared/CustomFileInput';

export default {
    components: {
        CustomFileInput,
        quillEditor,
    },
    props: {
        backendFiles: {
            type: [Array, String],
            default: () => ([]),
        },
        position: {
            type: String,
            default: '',
        },
        comment: {
            type: Object,
            default: null,
        },
        canUploadImages: {
            type: Boolean,
            default: true,
        },
        isLab: {
            type: Boolean,
            default: false,
        },
        workOrderId: {
            type: String,
            default: '',
        },
        isWOTemplate: {
            type: Boolean,
            default: false,
        },
        hidePictureSection: {
            type: Boolean,
            default: false,
        },
        showAddSentences: {
            type: Boolean,
            default: true,
        },
        showSaveBtn: {
            type: Boolean,
            default: true,
        },
        options: {
            type: Object,
            default: () => ({ }),
        },
        editorDataCy: {
            type: String,
            default: '',
        },
        fileInputDataCy: {
            type: String,
            default: '',
        },
        saveBtnDataCy: {
            type: String,
            default: '',
        },
        filesDataCy: {
            type: String,
            default: '',
        },
    },
    data() {
        return {
            isBtnDisabled: true,
            selectedImages: [],
            isUploading: false,
            message: '',
            files: [],
            mutableComment: {},
            editorKey: 0,
        };
    },
    computed: {
        ...mapGetters([
            'labWorkOrder',
            'hasPermission',
            'lang',
        ]),
        isDisabled() {
            return this.options.disabled;
        },
        getOptions() {
            return { ...this.options, placeholder: this.$t('forms.labels.placeholderComment') };
        },
    },
    watch: {
        comment: {
            handler(val) {
                this.mutableComment = val;
                if (this.mutableComment && this.mutableComment.message) {
                    this.message = this.mutableComment.message;
                }
                if (this.mutableComment && this.mutableComment.files) {
                    this.files = this.mutableComment.files;
                }
            },
            deep: true,
            immediate: true,
        },
        message: {
            handler() {
                this.isBtnDisabled = false;
            },
        },
        selectedImages() {
            this.isBtnDisabled = false;
        },
        lang: {
            handler() {
                this.editorKey += 1; // Incrémente la clé pour forcer le re-rendu
            },
            immediate: true,
            deep: true,
        },

    },
    methods: {
        async onSaveClick(workOrderId = this.workOrderId) {
            this.isUploading = true;
            if (this.isWOTemplate) {
                this.$emit('message', {
                    position: this.position,
                    message: this.message,
                });
                this.isBtnDisabled = true;
                this.isUploading = false;
                return;
            }
            const promises = [];
            this.selectedImages.forEach(imageFile => {
                const formData = new FormData();
                formData.append('file', imageFile);
                promises.push(this.addFile(formData));
            });

            const imageFileIds = await Promise.all(promises);

            const payload = {
                message: this.message,
                position: this.position,
            };
            if (promises.length) {
                payload.files = imageFileIds.map(({ id }) => id);
                this.files = payload.files;
                this.selectedImages = [];
            }
            if (!this.mutableComment || Object.keys(this.mutableComment).length === 0) {
                const { data } = this.isLab
                    ? await this.$rq.addLabWorkOrderComment(this.labWorkOrder.id, payload)
                    : await this.$rq.addComment(workOrderId, payload);
                this.mutableComment = data;
                this.$emit('comments');
            } else {
                const { data } = this.isLab
                    ? await this.$rq.updateLabWorkOrderComment(this.labWorkOrder.id, this.mutableComment.id, payload)
                    : await this.$rq.editComment(workOrderId, this.mutableComment.id, payload);
                this.mutableComment = data;
                this.$emit('comments');
            }
            this.isBtnDisabled = true;
            this.isUploading = false;
            this.$emit('onCommentSaved');
        },
        async addFile(formData) {
            const { data } = await this.$rq.addFile(null, formData);
            return data;
        },
        onAddAutoSentenceClick() {
            this.$root.$emit('showModal', {
                title: this.$t('visitSummary.addAutomaticSentence'),
                component: Vue.component('addAutomaticSentenceForm', AddAutomaticSentenceForm),
                onConfirm: async requestPayload => {
                    let comment = requestPayload;
                    if (typeof requestPayload !== 'string') {
                        comment = requestPayload.note;
                    }
                    if (comment) {
                        this.message += `<p>${comment}</p>`;
                    }
                },
            });
        },
        async removeFiles(fileId) {
            if (this.isLab) {
                await this.$rq.removeFilesToALabComment(this.mutableComment.id, this.labWorkOrder.id, fileId);
            } else {
                await this.$rq.removeFilesToAComment(this.mutableComment.id, this.workOrderId, fileId);
            }
            this.$emit('comments');
        },
    },
};
</script>

<style lang="scss" scoped>
  .global-comment {
    width: 100% !important;
  }
  .global-comment::v-deep {
    .ql-video, .ql-image {
      display: none !important;
    }
  }
</style>
